import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNoindexNofollow } from '../hooks/useNoindex';

const API = '/api/admin';
const AUTH_KEY = 'ps_admin_auth';
const BRAND_SEO_API = '/api/admin/brand-seo';

/* ==============================
   Вспомогательные
   ============================== */
function classNames(...xs) {
    return xs.filter(Boolean).join(' ');
}

function useToast() {
    const [toasts, setToasts] = useState([]);
    const push = useCallback((kind, text) => {
        const id = Date.now() + Math.random();
        setToasts((t) => [...t, { id, kind, text }]);
        setTimeout(() => {
            setToasts((t) => t.filter((x) => x.id !== id));
        }, 3500);
    }, []);
    const view = (
        <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
            {toasts.map((t) => (
                <div
                    key={t.id}
                    className={classNames(
                        'pointer-events-auto px-4 py-3 rounded-xl shadow-xl text-sm font-medium min-w-[240px]',
                        t.kind === 'success' && 'bg-[#6B7A6A] text-white',
                        t.kind === 'error' && 'bg-red-500 text-white',
                        t.kind === 'info' && 'bg-[#1C1917] text-white'
                    )}
                >
                    {t.text}
                </div>
            ))}
        </div>
    );
    return { push, view };
}

/* ==============================
   Авторизация
   ============================== */
function LoginGate({ onAuth }) {
    const [user, setUser] = useState('admin');
    const [pass, setPass] = useState('');
    const [err, setErr] = useState('');
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr('');
        setLoading(true);
        const header = 'Basic ' + btoa(`${user}:${pass}`);
        try {
            const r = await fetch(`${API}/meta`, { headers: { Authorization: header } });
            if (r.status === 401) {
                setErr('Неверный логин или пароль');
            } else if (!r.ok) {
                setErr('Ошибка сервера: ' + r.status);
            } else {
                sessionStorage.setItem(AUTH_KEY, header);
                onAuth(header);
            }
        } catch (e) {
            setErr('Сеть недоступна: ' + e.message);
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center p-4">
            <form
                onSubmit={submit}
                className="bg-white rounded-3xl shadow-xl border border-[#E8E3DD] p-8 w-full max-w-md"
            >
                <h1 className="serif text-3xl text-[#1C1917] mb-2">Админка</h1>
                <p className="text-sm text-gray-500 mb-6">Pure Synergy · только для администратора</p>

                <label className="block text-xs font-medium text-gray-600 mb-1">Логин</label>
                <input
                    value={user}
                    onChange={(e) => setUser(e.target.value)}
                    autoComplete="username"
                    className="w-full mb-4 p-3 border border-[#E8E3DD] rounded-lg text-sm focus:outline-none focus:border-[#6B7A6A]"
                />

                <label className="block text-xs font-medium text-gray-600 mb-1">Пароль</label>
                <input
                    type="password"
                    value={pass}
                    onChange={(e) => setPass(e.target.value)}
                    autoComplete="current-password"
                    className="w-full mb-4 p-3 border border-[#E8E3DD] rounded-lg text-sm focus:outline-none focus:border-[#6B7A6A]"
                />

                {err && <p className="text-red-500 text-sm mb-4">{err}</p>}

                <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 rounded-lg bg-[#6B7A6A] text-white font-medium hover:bg-[#5a6a59] disabled:opacity-50 transition"
                >
                    {loading ? 'Проверяю…' : 'Войти'}
                </button>
            </form>
        </div>
    );
}

/* ==============================
   Правка ячейки (inline)
   ============================== */
function EditableCell({
    value,
    onChange,
    type = 'text',
    placeholder = '',
    multiline = false,
    className = '',
    emptyHint = '',
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    const inputRef = useRef(null);

    useEffect(() => {
        setDraft(value);
    }, [value]);

    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
            if (type !== 'textarea') inputRef.current.select?.();
        }
    }, [editing, type]);

    const commit = () => {
        setEditing(false);
        if (draft !== value) onChange(draft);
    };
    const cancel = () => {
        setDraft(value);
        setEditing(false);
    };

    const handleKey = (e) => {
        if (e.key === 'Enter' && !multiline) { e.preventDefault(); commit(); }
        if (e.key === 'Escape') cancel();
        if (e.key === 'Tab') commit();
    };

    if (!editing) {
        return (
            <div
                onClick={() => setEditing(true)}
                className={classNames(
                    'cursor-text rounded px-2 py-1 -mx-2 -my-1 hover:bg-[#F5F0EB] transition min-h-[28px]',
                    !value && 'text-gray-400 italic',
                    className
                )}
                title="Нажмите, чтобы редактировать"
            >
                {value || emptyHint || '—'}
            </div>
        );
    }

    const commonCls = 'w-full p-1.5 border border-[#6B7A6A] rounded bg-white text-sm focus:outline-none';
    return multiline ? (
        <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={handleKey}
            placeholder={placeholder}
            rows={3}
            className={commonCls}
        />
    ) : (
        <input
            ref={inputRef}
            type={type}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={handleKey}
            placeholder={placeholder}
            className={commonCls}
        />
    );
}

/* ==============================
   Ячейка описания
   ============================== */
function DescriptionCell({ value, onChange }) {
    const [expanded, setExpanded] = useState(false);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value || '');
    const inputRef = useRef(null);

    useEffect(() => { setDraft(value || ''); }, [value]);
    useEffect(() => {
        if (editing && inputRef.current) inputRef.current.focus();
    }, [editing]);

    const commit = () => {
        setEditing(false);
        if (draft !== (value || '')) onChange(draft);
    };
    const cancel = () => {
        setDraft(value || '');
        setEditing(false);
    };

    if (editing) {
        return (
            <div className="flex flex-col gap-2">
                <textarea
                    ref={inputRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={12}
                    placeholder="Описание товара…"
                    className="w-full p-2 border border-[#6B7A6A] rounded bg-white text-xs focus:outline-none min-w-[400px]"
                />
                <div className="flex gap-2 items-center">
                    <button
                        onClick={commit}
                        className="px-3 py-1 rounded bg-[#6B7A6A] text-white text-xs hover:bg-[#5a6a59]"
                    >
                        Сохранить
                    </button>
                    <button
                        onClick={cancel}
                        className="px-3 py-1 rounded border border-[#E8E3DD] text-xs hover:bg-[#F5F0EB]"
                    >
                        Отмена
                    </button>
                    <span className="ml-auto text-gray-400">{draft.length} символов</span>
                </div>
            </div>
        );
    }

    const text = value || '';
    const isEmpty = !text.trim();

    return (
        <div>
            <div className={expanded ? '' : 'line-clamp-3'}>
                {text || <span className="text-gray-400 italic">— пусто —</span>}
            </div>
            <div className="mt-1 flex gap-3 text-xs items-center">
                {text.length > 150 && (
                    <button
                        onClick={() => setExpanded(!expanded)}
                        className="text-[#6B7A6A] hover:underline"
                    >
                        {expanded ? 'Свернуть' : 'Показать полностью'}
                    </button>
                )}
                <button
                    onClick={() => setEditing(true)}
                    className="text-[#6B7A6A] hover:underline"
                >
                    Редактировать
                </button>
                {isEmpty && <span className="text-red-500">не заполнено</span>}
            </div>
        </div>
    );
}

/* ==============================
Ячейка фото: превью, замена URL, детект мёртвой ссылки
============================== */
function PhotoCell({ value, onChange, onBroken }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value || '');
    const [broken, setBroken] = useState(false);
    useEffect(() => { setDraft(value || ''); }, [value]);

    const commit = () => {
        setEditing(false);
        const next = draft.trim();
        if (next !== (value || '')) {
            setBroken(false);
            onChange(next);          // → строка станет «грязной» (жёлтой)
        }
    };
    const handleKey = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
    };

    return (
        <div className="flex flex-col items-start gap-1">
            {value ? (
                <img
                    src={value}
                    alt=""
                    className={`w-14 h-14 rounded object-cover bg-[#F5F0EB] ${broken ? 'opacity-30 border border-red-300' : ''}`}
                    onError={() => { setBroken(true); onBroken?.(); }}
                    onLoad={() => setBroken(false)}
                />
            ) : (
                <div className="w-14 h-14 rounded bg-[#F5F0EB] flex items-center justify-center text-[9px] text-gray-400 text-center px-1">
                    нет фото
                </div>
            )}
            {broken && <span className="text-[10px] text-red-500 font-medium">ссылка мертва</span>}
            {editing ? (
                <div className="flex flex-col gap-1 w-44">
                    <input
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={handleKey}
                        placeholder="https://..."
                        autoFocus
                        className="w-full p-1 border border-[#6B7A6A] rounded text-xs focus:outline-none"
                    />
                    <div className="flex gap-1">
                        <button onClick={commit} className="px-2 py-0.5 rounded bg-[#6B7A6A] text-white text-[10px] hover:bg-[#5a6a59]">
                            ОК
                        </button>
                        <button
                            onClick={() => { setDraft(value || ''); setEditing(false); }}
                            className="px-2 py-0.5 rounded border border-[#E8E3DD] text-[10px] hover:bg-[#F5F0EB]"
                        >
                            Отмена
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    onClick={() => setEditing(true)}
                    className="text-[10px] text-[#6B7A6A] underline hover:text-[#1C1917]"
                >
                    {value ? 'заменить' : 'добавить фото'}
                </button>
            )}
        </div>
    );
}

/* ==============================
   Модальное окно создания товара
   ============================== */
function NewProductModal({ open, onClose, onCreate, brands, categories, headers, toastPush }) {
    const [form, setForm] = useState({
        id: '', name: '', price: '', category: '', brand: '',
        keywords: '', description: '', photo: '', volume: '', badges: '',
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    useEffect(() => {
        if (open) {
            setForm({
                id: '', name: '', price: '', category: '', brand: '',
                keywords: '', description: '', photo: '', volume: '', badges: '',
            });
            setErr('');
        }
    }, [open]);

    if (!open) return null;

    const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setErr('');
        if (!form.id.trim()) { setErr('Укажите ID товара'); return; }
        if (!form.name.trim()) { setErr('Укажите название'); return; }
        if (!form.id.trim().match(/^\d+$/)) { setErr('ID должен быть числом (как в VK Market)'); return; }
        const price = parseFloat(String(form.price).replace(',', '.'));
        if (!price || price <= 0) { setErr('Укажите корректную цену'); return; }

        setSaving(true);
        try {
            const body = {
                id: form.id.trim(),
                name: form.name.trim(),
                price: price,
                category: form.category.trim(),
                brand: form.brand.trim(),
                keywords: form.keywords.trim(),
                description: form.description.trim(),
                photo: form.photo.trim(),
                volume: form.volume.trim(),
                badges: form.badges.trim(),
            };
            const r = await fetch(`${API}/products`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!r.ok) {
                const txt = await r.text();
                throw new Error(txt);
            }
            const res = await r.json();
            toastPush('success', `Товар создан: ${body.name}`);
            onCreate(res);
            onClose();
        } catch (e) {
            setErr(e.message);
        }
        setSaving(false);
    };

    const inputCls = 'w-full p-2 border border-[#E8E3DD] rounded-lg text-sm focus:outline-none focus:border-[#6B7A6A]';
    const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-[9998] flex items-start justify-center p-4 overflow-y-auto"
            onClick={onClose}
        >
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
                className="bg-white rounded-2xl shadow-2xl border border-[#E8E3DD] w-full max-w-2xl my-8"
            >
                <div className="flex items-center justify-between p-4 border-b border-[#E8E3DD]">
                    <h2 className="serif text-xl">Новый товар</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                    >
                        ×
                    </button>
                </div>

                <div className="p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>ID товара (числовой) *</label>
                            <input
                                value={form.id}
                                onChange={setField('id')}
                                placeholder="например 14872727"
                                className={inputCls}
                            />
                            <p className="text-[10px] text-gray-400 mt-1">
                                Для товара в VK Market — его item_id. Для нового — любое свободное число.
                            </p>
                        </div>
                        <div>
                            <label className={labelCls}>Цена, ₽ *</label>
                            <input
                                value={form.price}
                                onChange={setField('price')}
                                placeholder="4900"
                                className={inputCls}
                            />
                        </div>
                    </div>

                    <div>
                        <label className={labelCls}>Название *</label>
                        <input
                            value={form.name}
                            onChange={setField('name')}
                            placeholder="СЫВОРОТКА С ВИТАМИНОМ С"
                            className={inputCls}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>Бренд</label>
                            <input
                                value={form.brand}
                                onChange={setField('brand')}
                                list="brands-list"
                                placeholder="Skin Synergy"
                                className={inputCls}
                            />
                            <datalist id="brands-list">
                                {brands.map((b) => <option key={b} value={b} />)}
                            </datalist>
                        </div>
                        <div>
                            <label className={labelCls}>Категория</label>
                            <input
                                value={form.category}
                                onChange={setField('category')}
                                list="categories-list"
                                placeholder="Сыворотки"
                                className={inputCls}
                            />
                            <datalist id="categories-list">
                                {categories.map((c) => <option key={c} value={c} />)}
                            </datalist>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>Объём</label>
                            <input
                                value={form.volume}
                                onChange={setField('volume')}
                                placeholder="30 мл"
                                className={inputCls}
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Метки (через запятую)</label>
                            <input
                                value={form.badges}
                                onChange={setField('badges')}
                                placeholder="БЕСТСЕЛЛЕР, НОВИНКА"
                                className={inputCls}
                            />
                        </div>
                    </div>

                    <div>
                        <label className={labelCls}>Ключевые слова (для бота)</label>
                        <textarea
                            value={form.keywords}
                            onChange={setField('keywords')}
                            rows={2}
                            placeholder="увлажнение, витамин С, осветление, антиоксидант"
                            className={inputCls}
                        />
                    </div>

                    <div>
                        <label className={labelCls}>Описание</label>
                        <textarea
                            value={form.description}
                            onChange={setField('description')}
                            rows={4}
                            placeholder="Компания-разработчик... Активные компоненты..."
                            className={inputCls}
                        />
                    </div>

                    <div>
                        <label className={labelCls}>Ссылка на фото (URL)</label>
                        <input
                            value={form.photo}
                            onChange={setField('photo')}
                            placeholder="https://..."
                            className={inputCls}
                        />
                    </div>

                    {err && (
                        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                            {err}
                        </div>
                    )}
                </div>

                <div className="flex gap-3 p-4 border-t border-[#E8E3DD]">
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-4 py-2 rounded-lg bg-[#6B7A6A] text-white text-sm font-medium hover:bg-[#5a6a59] disabled:opacity-50"
                    >
                        {saving ? 'Сохранение…' : 'Создать товар'}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg border border-[#E8E3DD] text-sm hover:bg-[#F5F0EB]"
                    >
                        Отмена
                    </button>
                </div>
            </form>
        </div>
    );
}

/* ==============================
   Диалог подтверждения
   ============================== */
function ConfirmDialog({ open, title, message, confirmLabel, onConfirm, onCancel, danger = false }) {
    if (!open) return null;
    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
            onClick={onCancel}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl shadow-2xl border border-[#E8E3DD] w-full max-w-md"
            >
                <div className="p-5">
                    <h2 className="serif text-xl mb-2">{title}</h2>
                    <div className="text-sm text-gray-700">{message}</div>
                </div>
                <div className="flex gap-3 p-4 border-t border-[#E8E3DD] justify-end">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 rounded-lg border border-[#E8E3DD] text-sm hover:bg-[#F5F0EB]"
                    >
                        Отмена
                    </button>
                    <button
                        onClick={onConfirm}
                        className={classNames(
                            'px-4 py-2 rounded-lg text-sm font-medium text-white',
                            danger ? 'bg-red-500 hover:bg-red-600' : 'bg-[#6B7A6A] hover:bg-[#5a6a59]'
                        )}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ==============================
   Строка товара
   ============================== */
function ProductRow({ product, onChange, onDelete, selected, onSelect, onBrokenPhoto }) {
    const dirty = product.__dirty === true;
    const keywordsEmpty = !(product.keywords || '').trim();

    const setField = (field) => (val) => {
        onChange({ ...product, [field]: val, __dirty: true });
    };

    return (
        <tr
            className={classNames(
                'border-b border-[#F0ECE8] transition',
                selected && 'bg-[#F5F0EB]',
                dirty && 'bg-amber-50'
            )}
        >
            <td className="p-2 text-center align-top">
                <input
                    type="checkbox"
                    checked={selected}
                    onChange={(e) => onSelect(e.target.checked)}
                    className="mt-1 cursor-pointer"
                />
            </td>
            <td className="p-2 text-xs text-gray-500 align-top font-mono whitespace-nowrap">
                {product.id}
            </td>
            <td className="p-2 align-top">
                <PhotoCell
                    value={product.photo}
                    onChange={setField('photo')}
                    onBroken={() => onBrokenPhoto?.(product.id)}
                />
            </td>
            <td className="p-2 align-top min-w-[260px]">
                <EditableCell value={product.name} onChange={setField('name')} />
                <div className="mt-1 flex gap-1 items-center text-[10px] text-gray-400">
                    <span className="px-1.5 py-0.5 bg-[#F5F0EB] rounded">{product.brand || '—'}</span>
                    <span className="px-1.5 py-0.5 bg-[#F5F0EB] rounded">{product.category || '—'}</span>
                    {product.volume && <span>{product.volume}</span>}
                </div>
            </td>
            <td className="p-2 align-top w-24">
                <EditableCell
                    value={String(product.price ?? '')}
                    onChange={(v) => setField('price')(parseFloat(v) || 0)}
                    type="number"
                    className="text-right font-medium"
                />
            </td>
            <td className="p-2 align-top w-28">
                <EditableCell value={product.category} onChange={setField('category')} />
            </td>
            <td className="p-2 align-top w-32">
                <EditableCell value={product.brand} onChange={setField('brand')} />
            </td>
            <td className="p-2 align-top min-w-[320px]">
                <div className="flex items-start gap-2">
                    {keywordsEmpty && (
                        <span
                            className="shrink-0 mt-1 text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded"
                            title="Ключевые слова не заполнены — бот не найдёт этот товар по смыслу"
                        >
                            !
                        </span>
                    )}
                    <div className="flex-1">
                        <EditableCell
                            value={product.keywords || ''}
                            onChange={setField('keywords')}
                            multiline
                            placeholder="увлажнение, барьер, сухая кожа, восстановление"
                        />
                    </div>
                </div>
            </td>
            <td className="p-2 align-top text-xs text-gray-500 min-w-[280px]">
                <DescriptionCell
                    value={product.description}
                    onChange={setField('description')}
                />
            </td>
            <td className="p-2 align-top text-center w-12">
                <button
                    onClick={onDelete}
                    title="Удалить товар"
                    className="text-red-400 hover:text-red-600 text-lg leading-none"
                >
                    🗑
                </button>
            </td>
        </tr>
    );
}

/* ==============================
   Бейдж статуса заказа
   НОВОЕ: расширенный набор статусов + русские подписи
   ============================== */
const ORDER_STATUS_LABELS = {
    CREATED: 'Создан',
    PENDING_PAYMENT: 'Ожидает оплаты',
    PAID: 'Оплачен',
    SHIPPED: 'Отправлен',
    DELIVERED: 'Доставлен',
    RETURN_REQUESTED: 'Возврат запрошен',
    RETURNED: 'Возврат',
    REFUNDED: 'Деньги возвращены',
    CANCELLED: 'Отменён',
};

const ORDER_STATUS_STYLES = {
    CREATED: 'bg-gray-100 text-gray-700',
    PENDING_PAYMENT: 'bg-amber-100 text-amber-800',
    PAID: 'bg-[#6B7A6A] text-white',
    SHIPPED: 'bg-blue-100 text-blue-800',
    DELIVERED: 'bg-emerald-100 text-emerald-800',
    RETURN_REQUESTED: 'bg-orange-100 text-orange-800',
    RETURNED: 'bg-purple-100 text-purple-800',
    REFUNDED: 'bg-purple-100 text-purple-700',
    CANCELLED: 'bg-red-100 text-red-700',
};

function OrderStatusBadge({ status }) {
    const cls = ORDER_STATUS_STYLES[status] || 'bg-gray-100 text-gray-700';
    const label = ORDER_STATUS_LABELS[status] || status || '—';
    return (
        <span className={classNames('px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap', cls)}>
            {label}
        </span>
    );
}

/* ==============================
   НОВОЕ: Выпадающий список для смены статуса
   ============================== */
function StatusDropdown({ orderId, currentStatus, onChange, disabled }) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [modal, setModal] = useState(null); // { value } — если открыта модалка трека
    const [tracking, setTracking] = useState('');
    const [service, setService] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const OPTIONS = [
        'CREATED', 'PENDING_PAYMENT', 'PAID', 'SHIPPED',
        'DELIVERED', 'RETURN_REQUESTED', 'RETURNED', 'REFUNDED', 'CANCELLED',
    ];

    const applyStatus = async (value, extra = {}) => {
        setSaving(true);
        try {
            await onChange(value, extra);
            setOpen(false);
            setModal(null);
            setTracking('');
            setService('');
        } catch (e) {
            /* toast уже показан в родителе */
        } finally {
            setSaving(false);
        }
    };

    const handleSelect = async (value) => {
        if (value === currentStatus) { setOpen(false); return; }
        if (value === 'SHIPPED') {
            // Открываем модалку для трека
            setModal({ value });
            setOpen(false);
            return;
        }
        await applyStatus(value);
    };

    return (
        <>
            <div className="relative inline-block" ref={ref}>
                <button
                    type="button"
                    onClick={() => !disabled && !saving && setOpen(!open)}
                    disabled={disabled || saving}
                    className={classNames(
                        'cursor-pointer rounded-full transition',
                        saving && 'opacity-60',
                        disabled && 'cursor-not-allowed'
                    )}
                    title="Кликните, чтобы изменить статус"
                >
                    <OrderStatusBadge status={currentStatus} />
                </button>

                {open && (
                    <div className="absolute right-0 top-full mt-1 bg-white border border-[#E8E3DD] rounded-lg shadow-2xl z-[100] min-w-[220px] py-1">
                        {OPTIONS.map((opt) => (
                            <button
                                key={opt}
                                type="button"
                                onClick={() => handleSelect(opt)}
                                className={classNames(
                                    'w-full text-left px-3 py-2 text-xs hover:bg-[#F5F0EB] transition flex items-center gap-2',
                                    opt === currentStatus && 'bg-[#F5F0EB] font-medium'
                                )}
                            >
                                <OrderStatusBadge status={opt} />
                                <span className="text-gray-700">{ORDER_STATUS_LABELS[opt]}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {modal && modal.value === 'SHIPPED' && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
                    onClick={() => !saving && setModal(null)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white rounded-2xl shadow-2xl border border-[#E8E3DD] w-full max-w-md p-5"
                    >
                        <h3 className="serif text-lg mb-3">
                            Отправка заказа {orderId}
                        </h3>
                        <p className="text-xs text-gray-500 mb-4">
                            Клиенту уйдёт письмо с трек-номером на его email.
                        </p>

                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            Трек-номер *
                        </label>
                        <input
                            value={tracking}
                            onChange={(e) => setTracking(e.target.value)}
                            placeholder="например 12345678901234"
                            autoFocus
                            className="w-full mb-3 p-2 border border-[#E8E3DD] rounded-lg text-sm focus:outline-none focus:border-[#6B7A6A]"
                        />

                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            Служба доставки (необязательно)
                        </label>
                        <select
                            value={service}
                            onChange={(e) => setService(e.target.value)}
                            className="w-full mb-4 p-2 border border-[#E8E3DD] rounded-lg text-sm bg-white focus:outline-none focus:border-[#6B7A6A]"
                        >
                            <option value="">— определить автоматически —</option>
                            <option value="Wildberries">Wildberries</option>
                            <option value="Ozon">Ozon</option>
                            <option value="Почта России">Почта России</option>
                            <option value="СДЭК">СДЭК</option>
                        </select>

                        <div className="flex gap-2 justify-end">
                            <button
                                type="button"
                                onClick={() => setModal(null)}
                                disabled={saving}
                                className="px-3 py-2 rounded-lg border border-[#E8E3DD] text-sm hover:bg-[#F5F0EB] disabled:opacity-50"
                            >
                                Отмена
                            </button>
                            <button
                                type="button"
                                onClick={() => applyStatus('SHIPPED', {
                                    tracking_number: tracking.trim(),
                                    delivery_service: service.trim(),
                                })}
                                disabled={saving || !tracking.trim()}
                                className="px-3 py-2 rounded-lg bg-[#6B7A6A] text-white text-sm font-medium hover:bg-[#5a6a59] disabled:opacity-50"
                            >
                                {saving ? 'Отправляю…' : 'Отправить и уведомить'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

/* ==============================
   Строка заказа
   НОВОЕ: принимает onStatusChange и рендерит StatusDropdown
   ============================== */
function OrderRow({ order, onStatusChange }) {
    const [expanded, setExpanded] = useState(false);
    const items = (order.items || '').split(';').map((s) => s.trim()).filter(Boolean);
    const hasMore = items.length > 2;

    return (
        <tr className="border-b border-[#F0ECE8] hover:bg-[#FBF9F6] transition">
            <td className="p-2 text-xs text-gray-500 align-top whitespace-nowrap">
                {order.created_at || '—'}
            </td>
            <td className="p-2 text-xs align-top font-mono whitespace-nowrap">
                {order.order_id}
            </td>
            <td className="p-2 align-top min-w-[180px]">
                <div className="font-medium text-sm">{order.customer_name || '—'}</div>
                <div className="text-xs text-gray-500">{order.phone || ''}</div>
                <div className="text-xs text-gray-500">{order.email || ''}</div>
            </td>
            <td className="p-2 align-top text-xs min-w-[300px]">
                <div className={expanded ? '' : 'line-clamp-2'}>
                    {items.length > 0
                        ? items.map((it, i) => <div key={i}>• {it}</div>)
                        : '—'}
                </div>
                {hasMore && (
                    <button
                        onClick={() => setExpanded(!expanded)}
                        className="text-[10px] text-[#6B7A6A] hover:underline mt-1"
                    >
                        {expanded ? 'Свернуть' : `Показать все (${items.length})`}
                    </button>
                )}
            </td>
            <td className="p-2 align-top text-right font-medium whitespace-nowrap">
                {order.amount ? `${order.amount.toLocaleString('ru-RU')} ₽` : '—'}
            </td>
            <td className="p-2 align-top text-xs min-w-[220px]">
                {order.delivery_address || '—'}
                {order.pickup_network && (
                    <div className="mt-0.5 text-[10px] text-gray-400 uppercase">
                        {order.pickup_network}
                    </div>
                )}
            </td>
            <td className="p-2 align-top">
                <StatusDropdown
                    orderId={order.order_id}
                    currentStatus={order.status}
                    onChange={(newStatus, extra) => onStatusChange(order.order_id, newStatus, extra)}
                />
            </td>
        </tr>
    );
}

/* ==============================
   Акции: форма создания
   ============================== */
function PromotionFormModal({ open, onClose, onSave, categories, brands, headers, toastPush }) {
    const [form, setForm] = useState({
        name: '', type: 'cart_total', target_id: '',
        discount_type: 'percent', discount_value: 10,
        starts_at: new Date().toISOString().slice(0, 10),
        ends_at: '',
        badge: '', priority: 0, stackable: false,
        min_amount: 20000,    // НОВОЕ
        min_quantity: 3,      // НОВОЕ
    });
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    useEffect(() => {
        if (open) {
            setForm({
                name: '', type: 'category', target_id: '',
                discount_type: 'percent', discount_value: 15,
                starts_at: new Date().toISOString().slice(0, 10),
                ends_at: '',
                badge: '', priority: 0, stackable: false,
            });
            setPreview(null);
            setErr('');
        }
    }, [open]);

    // Debounce предпросмотра
    useEffect(() => {
        if (!open || !form.target_id || form.discount_type !== 'percent') {
            setPreview(null);
            return;
        }
        const t = setTimeout(async () => {
            try {
                const qs = new URLSearchParams({
                    ptype: form.type,
                    target_id: form.target_id,
                    discount_value: String(form.discount_value),
                });
                const r = await fetch(`${API}/promotions/preview?${qs}`, { headers });
                if (r.ok) setPreview(await r.json());
            } catch (_) { }
        }, 500);
        return () => clearTimeout(t);
    }, [open, form.type, form.target_id, form.discount_value, form.discount_type, headers]);

    if (!open) return null;

    const setField = (k) => (e) => {
        const v = e?.target ? e.target.value : e;
        const checked = e?.target?.type === 'checkbox' ? e.target.checked : undefined;
        setForm((f) => ({ ...f, [k]: checked !== undefined ? checked : v }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setErr('');

        // Общие проверки
        if (!form.name.trim()) { setErr('Укажите название'); return; }
        if (form.discount_type === 'percent' && (form.discount_value <= 0 || form.discount_value > 25)) {
            setErr('Скидка должна быть от 1 до 25%'); return;
        }

        // Проверки по типу акции
        const isCart = ['cart_total', 'cart_quantity'].includes(form.type);

        if (isCart) {
            // Для корзинных акций — проверяем пороги
            if (form.type === 'cart_total' && (!form.min_amount || form.min_amount <= 0)) {
                setErr('Укажите минимальную сумму > 0'); return;
            }
            if (form.type === 'cart_quantity' && (!form.min_quantity || form.min_quantity <= 0)) {
                setErr('Укажите минимум разных товаров > 0'); return;
            }
        } else {
            // Для товарных акций — обязательно указать цель
            if (!form.target_id.trim()) {
                setErr('Укажите цель акции (slug категории, бренд или ID товара)');
                return;
            }
        }
        setSaving(true);
        try {
            const body = {
                ...form,
                starts_at: form.starts_at ? `${form.starts_at}T00:00:00` : '',
                ends_at: form.ends_at ? `${form.ends_at}T23:59:59` : '',
            };
            const r = await fetch(`${API}/promotions`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!r.ok) {
                const txt = await r.text().catch(() => '');
                throw new Error(txt || `Ошибка ${r.status}`);
            }
            const res = await r.json();
            if (res.warnings?.length) {
                res.warnings.forEach((w) => toastPush('info', w));
            }
            toastPush('success', 'Акция создана');
            onSave();
            onClose();
        } catch (e) {
            setErr(e.message);
        } finally {
            setSaving(false);
        }
    };

    const inputCls = 'w-full p-2 border border-[#E8E3DD] rounded-lg text-sm focus:outline-none focus:border-[#6B7A6A]';
    const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-[9998] flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl shadow-2xl border border-[#E8E3DD] w-full max-w-3xl my-8">
                <div className="flex items-center justify-between p-4 border-b border-[#E8E3DD]">
                    <h2 className="serif text-xl">Новая акция</h2>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
                </div>

                <div className="p-4 space-y-3">
                    <div>
                        <label className={labelCls}>Название акции (для отчётов)</label>
                        <input value={form.name} onChange={setField('name')} placeholder="Сентябрьская −15% на сыворотки" className={inputCls} />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>Тип акции</label>
                            <select value={form.type} onChange={setField('type')} className={inputCls}>
                                <optgroup label="Корзина">
                                    <option value="cart_total">Скидка от суммы корзины</option>
                                    <option value="cart_quantity">Скидка за N разных товаров</option>
                                </optgroup>
                                <optgroup label="Товары">
                                    <option value="category">Категория</option>
                                    <option value="brand">Бренд</option>
                                    <option value="product">Один товар</option>
                                    <option value="custom">Список ID</option>
                                </optgroup>
                            </select>
                        </div>
                        {/* Для товарных акций — target_id */}
                        {['category', 'brand', 'product', 'custom'].includes(form.type) && (
                            <div>
                                <label className={labelCls}>
                                    {form.type === 'category' ? 'Slug категории' :
                                        form.type === 'brand' ? 'Название бренда' :
                                            form.type === 'product' ? 'ID товара' : 'IDs через запятую'}
                                </label>
                                <input value={form.target_id} onChange={setField('target_id')} className={inputCls} />
                            </div>
                        )}
                    </div>

                    {/* НОВОЕ: поля для cart-акций */}
                    {form.type === 'cart_total' && (
                        <div>
                            <label className={labelCls}>Минимальная сумма корзины, ₽</label>
                            <input type="number" value={form.min_amount} onChange={setField('min_amount')} className={inputCls} placeholder="20000" />
                        </div>
                    )}
                    {form.type === 'cart_quantity' && (
                        <div>
                            <label className={labelCls}>Минимум разных товаров в корзине</label>
                            <input type="number" value={form.min_quantity} onChange={setField('min_quantity')} className={inputCls} placeholder="3" />
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>Тип скидки</label>
                            <select value={form.discount_type} onChange={setField('discount_type')} className={inputCls}>
                                <option value="percent">Процент</option>
                                <option value="fixed">Фикс. сумма</option>
                            </select>
                        </div>
                        <div>
                            <label className={labelCls}>Размер ({form.discount_type === 'percent' ? '%' : '₽'})</label>
                            <input type="number" value={form.discount_value} onChange={setField('discount_value')} className={inputCls} />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>Начало</label>
                            <input type="date" value={form.starts_at} onChange={setField('starts_at')} className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls}>Окончание (ФАС)</label>
                            <input type="date" value={form.ends_at} onChange={setField('ends_at')} className={inputCls} />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>Бейдж (опционально)</label>
                            <input value={form.badge} onChange={setField('badge')} placeholder="−15%" className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls}>Приоритет</label>
                            <input type="number" value={form.priority} onChange={setField('priority')} className={inputCls} />
                        </div>
                    </div>

                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={form.stackable} onChange={setField('stackable')} />
                        Суммировать с другими акциями
                    </label>

                    {preview && (
                        <div className="p-3 rounded-lg bg-[#F5F0EB] border border-[#E8E3DD]">
                            <p className="text-xs font-medium mb-2">
                                В акцию попадёт: <strong>{preview.matched_count}</strong> из {preview.total_products} ({preview.percent_of_catalog}% каталога)
                            </p>
                            <div className="space-y-1 text-xs">
                                {preview.samples.slice(0, 5).map((s) => (
                                    <div key={s.id} className="flex justify-between gap-2">
                                        <span className="truncate">{s.name}</span>
                                        <span className="whitespace-nowrap text-gray-500">
                                            {s.base_price} → <strong className="text-[#1C1917]">{s.new_price} ₽</strong>
                                        </span>
                                    </div>
                                ))}
                                {preview.matched_count > 5 && (
                                    <p className="text-gray-500">…и ещё {preview.matched_count - 5}</p>
                                )}
                            </div>
                        </div>
                    )}

                    {err && (
                        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>
                    )}
                </div>

                <div className="flex gap-3 p-4 border-t border-[#E8E3DD]">
                    <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-[#6B7A6A] text-white text-sm font-medium hover:bg-[#5a6a59] disabled:opacity-50">
                        {saving ? 'Сохранение…' : 'Создать акцию'}
                    </button>
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-[#E8E3DD] text-sm hover:bg-[#F5F0EB]">Отмена</button>
                </div>
            </form>
        </div>
    );
}

/* ==============================
   Акции: вкладка со списком
   ============================== */
function PromotionsTab({ headers, meta, toastPush }) {
    const [data, setData] = useState({ active: [], pending: [], archive: [], total: 0 });
    const [loading, setLoading] = useState(false);
    const [showNew, setShowNew] = useState(false);

    const fetchPromotions = useCallback(async () => {
        setLoading(true);
        try {
            const r = await fetch(`${API}/promotions`, { headers });
            if (!r.ok) throw new Error(`Сервер вернул ${r.status}`);
            setData(await r.json());
        } catch (e) {
            toastPush('error', 'Ошибка загрузки акций: ' + e.message);
        }
        setLoading(false);
    }, [headers, toastPush]);

    useEffect(() => { fetchPromotions(); }, [fetchPromotions]);

    const togglePromo = async (id) => {
        try {
            const r = await fetch(`${API}/promotions/${id}/toggle`, { method: 'POST', headers });
            if (!r.ok) throw new Error(await r.text());
            toastPush('success', 'Акция обновлена');
            fetchPromotions();
        } catch (e) { toastPush('error', e.message); }
    };

    const deletePromo = async (id) => {
        if (!window.confirm('Удалить акцию? Действие необратимо.')) return;
        try {
            const r = await fetch(`${API}/promotions/${id}`, { method: 'DELETE', headers });
            if (!r.ok) throw new Error(await r.text());
            toastPush('success', 'Акция удалена');
            fetchPromotions();
        } catch (e) { toastPush('error', e.message); }
    };

    const renderList = (items) => (
        <div className="space-y-2">
            {items.length === 0 && <p className="text-xs text-gray-400 py-3">Пусто</p>}
            {items.map((p) => (
                <div key={p.id} className="flex items-center gap-3 p-3 border border-[#E8E3DD] rounded-lg bg-white">
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1C1917] truncate">{p.name}</p>
                        <p className="text-xs text-gray-500 truncate">
                            {p.type} · {p.target_id} · {p.discount_type === 'percent' ? `${p.discount_value}%` : `${p.discount_value} ₽`}
                            {p.ends_at && <> · до {p.ends_at.slice(0, 10)}</>}
                        </p>
                    </div>
                    <div className="text-xs text-gray-500 whitespace-nowrap">{p.badge}</div>
                    <button onClick={() => togglePromo(p.id)} className={`px-3 py-1.5 rounded-lg text-xs ${p.active ? 'bg-amber-100 text-amber-800' : 'bg-[#6B7A6A] text-white'}`}>
                        {p.active ? 'Выкл' : 'Вкл'}
                    </button>
                    <button onClick={() => deletePromo(p.id)} className="text-red-400 hover:text-red-600 text-lg">🗑</button>
                </div>
            ))}
        </div>
    );

    return (
        <div className="space-y-6">
            {/* НОВОЕ: быстрые пресеты */}
            <div className="bg-white rounded-2xl border border-[#E8E3DD] p-4">
                <p className="text-xs font-semibold text-gray-600 mb-3">⚡ Быстрое создание</p>
                <div className="flex flex-wrap gap-2">
                    {[
                        { name: 'Скидка 10% от 20 000 ₽', type: 'cart_total', value: 10, min_amount: 20000 },
                        { name: 'Скидка 10% от 30 000 ₽', type: 'cart_total', value: 10, min_amount: 30000 },
                        { name: 'Скидка 5% от 15 000 ₽', type: 'cart_total', value: 5, min_amount: 15000 },
                        { name: 'Скидка 10% за 3 разных товара', type: 'cart_quantity', value: 10, min_quantity: 3 },
                        { name: 'Скидка 15% за 5 разных товаров', type: 'cart_quantity', value: 15, min_quantity: 5 },
                        { name: 'Скидка 5% за 2 разных товара', type: 'cart_quantity', value: 5, min_quantity: 2 },
                    ].map((preset) => (
                        <button
                            key={preset.name}
                            type="button"
                            onClick={async () => {
                                if (!window.confirm(`Создать акцию «${preset.name}»?`)) return;
                                try {
                                    const r = await fetch(`${API}/promotions`, {
                                        method: 'POST',
                                        headers: { ...headers, 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                            name: preset.name,
                                            type: preset.type,
                                            target_id: '',
                                            discount_type: 'percent',
                                            discount_value: preset.value,
                                            starts_at: new Date().toISOString(),
                                            ends_at: '',
                                            min_amount: preset.min_amount || 0,
                                            min_quantity: preset.min_quantity || 0,
                                        }),
                                    });
                                    if (!r.ok) throw new Error(await r.text());
                                    toastPush('success', 'Акция создана');
                                    fetchPromotions();
                                } catch (e) {
                                    toastPush('error', e.message);
                                }
                            }}
                            className="px-3 py-2 rounded-lg border border-[#E8E3DD] hover:border-[#6B7A6A] hover:bg-[#F5F0EB] text-xs font-medium transition"
                        >
                            {preset.name}
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex items-center gap-3">
                <button onClick={() => setShowNew(true)} className="px-4 py-2 rounded-lg bg-[#6B7A6A] text-white text-sm font-medium hover:bg-[#5a6a59]">
                    + Создать акцию вручную
                </button>
                <button onClick={fetchPromotions} disabled={loading} className="px-3 py-2 rounded-lg border border-[#E8E3DD] text-xs hover:bg-[#F5F0EB]">
                    {loading ? '…' : 'Обновить'}
                </button>
                <span className="ml-auto text-xs text-gray-500">Всего: {data.total}</span>
            </div>

            <div className="bg-white rounded-2xl border border-[#E8E3DD] p-4">
                <h3 className="text-sm font-semibold mb-3 text-[#6B7A6A]">🔥 Активные ({data.active.length})</h3>
                {renderList(data.active)}
            </div>

            {data.pending.length > 0 && (
                <div className="bg-white rounded-2xl border border-[#E8E3DD] p-4">
                    <h3 className="text-sm font-semibold mb-3 text-blue-600">📅 Запланированные ({data.pending.length})</h3>
                    {renderList(data.pending)}
                </div>
            )}

            {data.archive.length > 0 && (
                <div className="bg-white rounded-2xl border border-[#E8E3DD] p-4">
                    <h3 className="text-sm font-semibold mb-3 text-gray-500">🗄 Архив ({data.archive.length})</h3>
                    {renderList(data.archive)}
                </div>
            )}

            <PromotionFormModal
                open={showNew}
                onClose={() => setShowNew(false)}
                onSave={fetchPromotions}
                categories={meta?.categories || []}
                brands={meta?.brands || []}
                headers={headers}
                toastPush={toastPush}
            />
        </div>
    );
}

/* ==============================
   ВКЛАДКА «КЛИЕНТЫ»
   ============================== */

const CLIENTS_API = '/api/admin/clients';

function fmtDate(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('ru-RU', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch { return '—'; }
}

function fmtRub(n) {
    const v = Number(n) || 0;
    return v.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₽';
}

function ClientDetailModal({ email, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');

    useEffect(() => {
        let stop = false;
        setLoading(true);
        fetch(`${CLIENTS_API}/by-email/${encodeURIComponent(email)}`, {
            credentials: 'include',
        })
            .then((r) => { if (!r.ok) throw new Error('Ошибка загрузки'); return r.json(); })
            .then((d) => { if (!stop) setData(d); })
            .catch((e) => { if (!stop) setErr(e.message || 'Ошибка'); })
            .finally(() => { if (!stop) setLoading(false); });
        return () => { stop = true; };
    }, [email]);

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={onClose}
        >
            <div
                className="bg-white w-full sm:rounded-2xl rounded-t-3xl shadow-2xl max-w-3xl max-h-[92vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 bg-white border-b border-[#E8E3DD] px-6 py-4 flex items-center justify-between z-10">
                    <div>
                        <p className="text-xs text-gray-500">Карточка клиента</p>
                        <p className="text-sm font-medium">{email}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
                </div>

                <div className="p-6 space-y-5">
                    {loading && <p className="text-sm text-gray-500">Загрузка…</p>}
                    {err && <p className="text-sm text-red-500">{err}</p>}

                    {data && (
                        <>
                            {/* Профиль */}
                            <div className="bg-[#FBF9F6] rounded-xl p-4">
                                <p className="text-xs text-gray-500 mb-2">Профиль</p>
                                <div className="text-sm space-y-1">
                                    <p><span className="text-gray-500">ФИО:</span> {data.profile.full_name || '—'}</p>
                                    <p><span className="text-gray-500">Телефон:</span> {data.profile.phone || '—'}</p>
                                    <p><span className="text-gray-500">Адрес:</span> {data.profile.address || '—'}</p>
                                </div>
                            </div>

                            {/* Заказы */}
                            <div>
                                <p className="text-sm font-semibold mb-2">
                                    Заказы ({data.orders.length})
                                </p>
                                {data.orders.length === 0 && (
                                    <p className="text-xs text-gray-400">Заказов нет</p>
                                )}
                                <div className="space-y-2">
                                    {data.orders.map((o) => (
                                        <div key={o.order_id} className="flex items-center justify-between gap-3 text-sm py-2 border-b border-[#F0ECE8] last:border-0">
                                            <div className="min-w-0">
                                                <p className="font-mono text-xs">{o.order_id}</p>
                                                <p className="text-[10px] text-gray-500">{fmtDate(o.created_at)}</p>
                                            </div>
                                            <div className="flex items-center gap-3 shrink-0">
                                                <span className="text-xs text-gray-500">{o.status}</span>
                                                <span className="font-medium">{fmtRub(o.amount_rub)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Логи входов */}
                            <div>
                                <p className="text-sm font-semibold mb-2">
                                    История входов ({data.login_events.length})
                                </p>
                                <div className="max-h-60 overflow-y-auto space-y-1">
                                    {data.login_events.map((e, i) => (
                                        <div key={i} className="flex items-start justify-between gap-3 text-xs py-1.5 border-b border-[#F0ECE8] last:border-0">
                                            <div>
                                                <span className={
                                                    'inline-block px-1.5 py-0.5 rounded text-[10px] font-medium mr-2 ' +
                                                    (e.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')
                                                }>
                                                    {e.event_type}
                                                </span>
                                                <span className="text-gray-500 font-mono">{e.ip}</span>
                                            </div>
                                            <span className="text-gray-500 whitespace-nowrap">{fmtDate(e.created_at)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function ClientsTab({ headers, toastPush }) {
    const [data, setData] = useState({ count: 0, items: [] });
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [activeDays, setActiveDays] = useState(0);
    const [marketingOnly, setMarketingOnly] = useState(false);
    const [sortBy, setSortBy] = useState('last_login');
    const [detailEmail, setDetailEmail] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const qs = new URLSearchParams();
            if (search) qs.set('search', search);
            if (activeDays > 0) qs.set('active_days', String(activeDays));
            if (marketingOnly) qs.set('marketing_only', 'true');
            qs.set('sort_by', sortBy);
            qs.set('limit', '500');

            const [r, rs] = await Promise.all([
                fetch(`${CLIENTS_API}?${qs}`, { headers }),
                fetch(`${CLIENTS_API}/stats`, { headers }),
            ]);
            if (r.status === 401 || rs.status === 401) {
                toastPush('error', 'Сессия истекла');
                return;
            }
            const d = await r.json();
            const s = await rs.json();
            setData(d);
            setStats(s);
        } catch (e) {
            toastPush('error', 'Ошибка загрузки клиентов: ' + e.message);
        } finally {
            setLoading(false);
        }
    }, [search, activeDays, marketingOnly, sortBy, headers, toastPush]);

    useEffect(() => { load(); }, [load]);

    const exportCsv = () => {
        const rows = [
            ['Email', 'Имя', 'Телефон', 'Заказов', 'Сумма, ₽', 'Средний чек, ₽', 'Последний заказ', 'Последний вход', 'Входов', 'Маркетинг'],
            ...data.items.map((c) => [
                c.email,
                c.full_name || '',
                c.phone || '',
                c.orders_count,
                c.total_rub,
                c.avg_check_rub,
                c.last_order_at || '',
                c.last_login_at || '',
                c.logins_count,
                c.marketing_consent ? 'да' : 'нет',
            ]),
        ];
        const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `clients-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toastPush('success', 'CSV скачан');
    };

    return (
        <>
            {/* KPI */}
            {stats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                    <div className="bg-white rounded-xl border border-[#E8E3DD] p-4">
                        <p className="text-xs text-gray-500 mb-1">Всего клиентов</p>
                        <p className="text-2xl font-semibold text-[#1C1917]">{stats.total_verified_clients}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-[#E8E3DD] p-4">
                        <p className="text-xs text-gray-500 mb-1">Активны за 30 дней</p>
                        <p className="text-2xl font-semibold text-[#6B7A6A]">{stats.active_30d}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-[#E8E3DD] p-4">
                        <p className="text-xs text-gray-500 mb-1">Активны за 7 дней</p>
                        <p className="text-2xl font-semibold text-[#6B7A6A]">{stats.active_7d}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-[#E8E3DD] p-4">
                        <p className="text-xs text-gray-500 mb-1">Подписаны на маркетинг</p>
                        <p className="text-2xl font-semibold text-[#1C1917]">{stats.marketing_subscribed}</p>
                    </div>
                </div>
            )}

            {/* Фильтры */}
            <div className="bg-white rounded-2xl border border-[#E8E3DD] p-4 mb-4 flex flex-wrap items-center gap-3">
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Поиск по email, имени, телефону…"
                    className="flex-1 min-w-[220px] p-2 border border-[#E8E3DD] rounded-lg text-sm focus:outline-none focus:border-[#6B7A6A]"
                />
                <select
                    value={activeDays}
                    onChange={(e) => setActiveDays(Number(e.target.value))}
                    className="p-2 border border-[#E8E3DD] rounded-lg text-sm bg-white"
                >
                    <option value={0}>Все</option>
                    <option value={7}>Заходили за 7 дней</option>
                    <option value={30}>Заходили за 30 дней</option>
                    <option value={90}>Заходили за 90 дней</option>
                </select>
                <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="p-2 border border-[#E8E3DD] rounded-lg text-sm bg-white"
                >
                    <option value="last_login">По последнему входу</option>
                    <option value="last_order">По последнему заказу</option>
                    <option value="orders">По кол-ву заказов</option>
                    <option value="amount">По сумме</option>
                </select>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                        type="checkbox"
                        checked={marketingOnly}
                        onChange={(e) => setMarketingOnly(e.target.checked)}
                    />
                    Только маркетинг
                </label>
                <button
                    onClick={load}
                    disabled={loading}
                    className="px-3 py-2 rounded-lg border border-[#E8E3DD] text-xs hover:bg-[#F5F0EB] disabled:opacity-50"
                >
                    {loading ? '…' : 'Обновить'}
                </button>
                <button
                    onClick={exportCsv}
                    className="px-3 py-2 rounded-lg bg-[#6B7A6A] text-white text-xs font-medium hover:bg-[#5a6a59]"
                >
                    📥 Экспорт CSV
                </button>
            </div>

            {/* Таблица */}
            <div className="bg-white rounded-2xl border border-[#E8E3DD] overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#FBF9F6] text-xs text-gray-500 uppercase tracking-wider">
                            <tr>
                                <th className="text-left p-3 font-medium">Клиент</th>
                                <th className="text-left p-3 font-medium">Телефон</th>
                                <th className="text-right p-3 font-medium">Заказов</th>
                                <th className="text-right p-3 font-medium">Сумма</th>
                                <th className="text-right p-3 font-medium">Средний чек</th>
                                <th className="text-left p-3 font-medium">Посл. заказ</th>
                                <th className="text-left p-3 font-medium">Посл. вход</th>
                                <th className="text-center p-3 font-medium">📩</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={8} className="text-center py-12 text-gray-400">
                                        Клиентов не найдено
                                    </td>
                                </tr>
                            )}
                            {data.items.map((c) => (
                                <tr
                                    key={c.email}
                                    className="border-b border-[#F0ECE8] hover:bg-[#FBF9F6] transition cursor-pointer"
                                    onClick={() => setDetailEmail(c.email)}
                                >
                                    <td className="p-3">
                                        <p className="font-medium text-[#1C1917]">{c.email}</p>
                                        {c.full_name && <p className="text-xs text-gray-500 truncate max-w-[220px]">{c.full_name}</p>}
                                    </td>
                                    <td className="p-3 text-xs text-gray-600 whitespace-nowrap">
                                        {c.phone || '—'}
                                    </td>
                                    <td className="p-3 text-right">
                                        {c.orders_count > 0 ? c.orders_count : <span className="text-gray-300">0</span>}
                                    </td>
                                    <td className="p-3 text-right font-medium">
                                        {c.total_rub > 0 ? fmtRub(c.total_rub) : <span className="text-gray-300">—</span>}
                                    </td>
                                    <td className="p-3 text-right text-xs text-gray-500">
                                        {c.avg_check_rub > 0 ? fmtRub(c.avg_check_rub) : '—'}
                                    </td>
                                    <td className="p-3 text-xs text-gray-500 whitespace-nowrap">
                                        {fmtDate(c.last_order_at)}
                                    </td>
                                    <td className="p-3 text-xs text-gray-500 whitespace-nowrap">
                                        {fmtDate(c.last_login_at)}
                                        {c.logins_count > 0 && (
                                            <span className="block text-[10px] text-gray-400">{c.logins_count} входов</span>
                                        )}
                                    </td>
                                    <td className="p-3 text-center">
                                        {c.marketing_consent ? (
                                            <span title="Подписан на рассылку" className="text-[#6B7A6A]">✓</span>
                                        ) : (
                                            <span className="text-gray-300">—</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <p className="text-xs text-gray-400 mt-3">
                Показано: {data.items.length} из {data.count}. Клик по строке — детали.
            </p>

            {detailEmail && (
                <ClientDetailModal email={detailEmail} onClose={() => setDetailEmail(null)} />
            )}
        </>
    );
}

/* ==============================
   ВКЛАДКА «БОНУСЫ»
   ============================== */

const BONUS_API = '/api/admin/bonus';

const BONUS_TYPES = {
    earn: { label: 'Начисление за заказ', color: 'text-emerald-600', sign: '+' },
    spend: { label: 'Списание при оплате', color: 'text-red-500', sign: '' },
    reversal: { label: 'Аннулирование (отмена)', color: 'text-red-500', sign: '' },
    expire: { label: 'Сгорело', color: 'text-gray-500', sign: '' },
    manual_credit: { label: 'Начислено админом', color: 'text-emerald-600', sign: '+' },
    manual_debit: { label: 'Списано админом', color: 'text-red-500', sign: '' },
    refund: { label: 'Возврат', color: 'text-emerald-600', sign: '+' },
};

function fmtBonusDate(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('ru-RU', {
            day: '2-digit', month: 'short', year: '2-digit',
            hour: '2-digit', minute: '2-digit',
        });
    } catch { return '—'; }
}

function ManualBonusModal({ open, onClose, onSuccess, headers, toastPush }) {
    const [email, setEmail] = useState('');
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');
    const [mode, setMode] = useState('credit');   // credit | debit
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const [lookup, setLookup] = useState(null);

    useEffect(() => {
        if (open) {
            setEmail(''); setAmount(''); setReason('');
            setMode('credit'); setErr(''); setLookup(null);
        }
    }, [open]);

    // Автоподсказка баланса по email
    useEffect(() => {
        if (!open || !email.includes('@')) { setLookup(null); return; }
        const t = setTimeout(async () => {
            try {
                const r = await fetch(`${BONUS_API}/balance?email=${encodeURIComponent(email)}`, { headers });
                if (r.ok) setLookup(await r.json());
                else setLookup(null);
            } catch (_) { setLookup(null); }
        }, 500);
        return () => clearTimeout(t);
    }, [email, open, headers]);

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        setErr('');
        const emailTrim = email.trim().toLowerCase();
        if (!emailTrim.includes('@')) { setErr('Укажите корректный email'); return; }
        const amtRaw = parseInt(String(amount).replace(/\s/g, ''), 10);
        if (!amtRaw || amtRaw <= 0) { setErr('Сумма должна быть положительной'); return; }

        const finalAmount = mode === 'credit' ? amtRaw : -amtRaw;
        setSaving(true);
        try {
            const r = await fetch(`${BONUS_API}/manual`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: emailTrim, amount: finalAmount, reason: reason.trim() }),
            });
            if (!r.ok) {
                const txt = await r.text();
                throw new Error(txt);
            }
            const res = await r.json();
            toastPush('success', `${mode === 'credit' ? 'Начислено' : 'Списано'} ${amtRaw} баллов. Баланс: ${res.new_balance}`);
            onSuccess();
            onClose();
        } catch (e) {
            setErr(e.message || 'Ошибка операции');
        } finally {
            setSaving(false);
        }
    };

    const inputCls = 'w-full p-2 border border-[#E8E3DD] rounded-lg text-sm focus:outline-none focus:border-[#6B7A6A]';
    const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-[9998] flex items-start justify-center p-4 overflow-y-auto"
            onClick={onClose}
        >
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
                className="bg-white rounded-2xl shadow-2xl border border-[#E8E3DD] w-full max-w-md my-8"
            >
                <div className="flex items-center justify-between p-4 border-b border-[#E8E3DD]">
                    <h2 className="serif text-xl">Ручная операция с бонусами</h2>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
                </div>

                <div className="p-4 space-y-3">
                    <div>
                        <label className={labelCls}>Email клиента *</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="client@example.com"
                            className={inputCls}
                            autoFocus
                        />
                        {lookup && (
                            <p className="text-[11px] text-gray-500 mt-1">
                                Найден: баланс <strong className="text-[#6B7A6A]">💎 {lookup.balance}</strong>
                            </p>
                        )}
                    </div>

                    <div>
                        <label className={labelCls}>Тип операции</label>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setMode('credit')}
                                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition ${mode === 'credit' ? 'bg-[#6B7A6A] text-white border-[#6B7A6A]' : 'bg-white border-[#E8E3DD]'}`}
                            >
                                + Начислить
                            </button>
                            <button
                                type="button"
                                onClick={() => setMode('debit')}
                                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition ${mode === 'debit' ? 'bg-red-500 text-white border-red-500' : 'bg-white border-[#E8E3DD]'}`}
                            >
                                − Списать
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className={labelCls}>Сумма, баллов *</label>
                        <input
                            type="number"
                            min="1"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="100"
                            className={inputCls}
                        />
                    </div>

                    <div>
                        <label className={labelCls}>Причина (опционально)</label>
                        <input
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Компенсация, акция, ручная правка"
                            className={inputCls}
                        />
                    </div>

                    {err && (
                        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>
                    )}
                </div>

                <div className="flex gap-3 p-4 border-t border-[#E8E3DD]">
                    <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-[#6B7A6A] text-white text-sm font-medium hover:bg-[#5a6a59] disabled:opacity-50">
                        {saving ? 'Обработка…' : (mode === 'credit' ? 'Начислить' : 'Списать')}
                    </button>
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-[#E8E3DD] text-sm hover:bg-[#F5F0EB]">
                        Отмена
                    </button>
                </div>
            </form>
        </div>
    );
}


function BonusTab({ headers, toastPush }) {
    const [data, setData] = useState({ count: 0, items: [] });
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [days, setDays] = useState(0);
    const [showManual, setShowManual] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const qs = new URLSearchParams();
            if (search) qs.set('search', search);
            if (typeFilter) qs.set('type_filter', typeFilter);
            if (days > 0) qs.set('days', String(days));
            qs.set('limit', '500');

            const [r, rs] = await Promise.all([
                fetch(`${BONUS_API}/transactions?${qs}`, { headers }),
                fetch(`${BONUS_API}/stats`, { headers }),
            ]);
            if (r.status === 401 || rs.status === 401) {
                toastPush('error', 'Сессия истекла');
                return;
            }
            setData(await r.json());
            setStats(await rs.json());
        } catch (e) {
            toastPush('error', 'Ошибка загрузки бонусов: ' + e.message);
        } finally {
            setLoading(false);
        }
    }, [search, typeFilter, days, headers, toastPush]);

    useEffect(() => { load(); }, [load]);

    return (
        <>
            {/* KPI */}
            {stats && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                    <div className="bg-white rounded-xl border border-[#E8E3DD] p-4">
                        <p className="text-xs text-gray-500 mb-1">Начислено всего</p>
                        <p className="text-xl font-semibold text-emerald-600">+{stats.total_earned}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-[#E8E3DD] p-4">
                        <p className="text-xs text-gray-500 mb-1">Списано</p>
                        <p className="text-xl font-semibold text-red-500">−{stats.total_spent}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-[#E8E3DD] p-4">
                        <p className="text-xs text-gray-500 mb-1">Аннулировано</p>
                        <p className="text-xl font-semibold text-gray-600">−{stats.total_reversed}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-[#E8E3DD] p-4">
                        <p className="text-xs text-gray-500 mb-1">Сгорело</p>
                        <p className="text-xl font-semibold text-gray-500">−{stats.total_expired}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-[#E8E3DD] p-4">
                        <p className="text-xs text-gray-500 mb-1">На балансах</p>
                        <p className="text-xl font-semibold text-[#6B7A6A]">💎 {stats.total_on_balance}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-[#E8E3DD] p-4">
                        <p className="text-xs text-gray-500 mb-1">Держателей</p>
                        <p className="text-xl font-semibold text-[#1C1917]">{stats.active_holders}</p>
                    </div>
                </div>
            )}

            {/* Фильтры */}
            <div className="bg-white rounded-2xl border border-[#E8E3DD] p-4 mb-4 flex flex-wrap items-center gap-3">
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Поиск по email, номеру заказа, причине…"
                    className="flex-1 min-w-[220px] p-2 border border-[#E8E3DD] rounded-lg text-sm focus:outline-none focus:border-[#6B7A6A]"
                />
                <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="p-2 border border-[#E8E3DD] rounded-lg text-sm bg-white"
                >
                    <option value="">Все типы</option>
                    {Object.entries(BONUS_TYPES).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                    ))}
                </select>
                <select
                    value={days}
                    onChange={(e) => setDays(Number(e.target.value))}
                    className="p-2 border border-[#E8E3DD] rounded-lg text-sm bg-white"
                >
                    <option value={0}>Все время</option>
                    <option value={7}>7 дней</option>
                    <option value={30}>30 дней</option>
                    <option value={90}>90 дней</option>
                </select>
                <button
                    onClick={load}
                    disabled={loading}
                    className="px-3 py-2 rounded-lg border border-[#E8E3DD] text-xs hover:bg-[#F5F0EB] disabled:opacity-50"
                >
                    {loading ? '…' : 'Обновить'}
                </button>
                <button
                    onClick={() => setShowManual(true)}
                    className="px-3 py-2 rounded-lg bg-[#6B7A6A] text-white text-xs font-medium hover:bg-[#5a6a59]"
                >
                    + Ручная операция
                </button>
            </div>

            {/* Таблица */}
            <div className="bg-white rounded-2xl border border-[#E8E3DD] overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#FBF9F6] text-xs text-gray-500 uppercase tracking-wider">
                            <tr>
                                <th className="text-left p-3 font-medium">Дата</th>
                                <th className="text-left p-3 font-medium">Клиент</th>
                                <th className="text-left p-3 font-medium">Тип</th>
                                <th className="text-right p-3 font-medium">Сумма</th>
                                <th className="text-right p-3 font-medium">Баланс после</th>
                                <th className="text-left p-3 font-medium">Заказ</th>
                                <th className="text-left p-3 font-medium">Причина</th>
                                <th className="text-left p-3 font-medium">Кто</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={8} className="text-center py-12 text-gray-400">
                                        Операций не найдено
                                    </td>
                                </tr>
                            )}
                            {data.items.map((t) => {
                                const meta = BONUS_TYPES[t.type] || { label: t.type, color: 'text-gray-700' };
                                const positive = t.amount > 0;
                                return (
                                    <tr key={t.id} className="border-b border-[#F0ECE8]">
                                        <td className="p-3 text-xs text-gray-500 whitespace-nowrap">
                                            {fmtBonusDate(t.created_at)}
                                        </td>
                                        <td className="p-3 text-xs">{t.email}</td>
                                        <td className="p-3 text-xs">
                                            <span className={meta.color}>{meta.label}</span>
                                        </td>
                                        <td className={`p-3 text-right font-medium ${positive ? 'text-emerald-600' : 'text-red-500'}`}>
                                            {positive ? '+' : ''}{t.amount}
                                        </td>
                                        <td className="p-3 text-right text-xs text-gray-500">
                                            {t.balance_after}
                                        </td>
                                        <td className="p-3 text-xs font-mono text-gray-500">
                                            {t.order_id || '—'}
                                        </td>
                                        <td className="p-3 text-xs text-gray-600 max-w-[240px] truncate" title={t.reason}>
                                            {t.reason || '—'}
                                        </td>
                                        <td className="p-3 text-xs text-gray-400">
                                            {t.created_by}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <p className="text-xs text-gray-400 mt-3">
                Показано: {data.items.length} из {data.count}.
            </p>

            <ManualBonusModal
                open={showManual}
                onClose={() => setShowManual(false)}
                onSuccess={load}
                headers={headers}
                toastPush={toastPush}
            />
        </>
    );
}

/* ==============================
ВКЛАДКА «БРЕНДЫ» — SEO через ИИ
============================== */
function BrandsTab({ headers, toastPush }) {
    const [brands, setBrands] = useState([]);
    const [seoMap, setSeoMap] = useState({});
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState({});   // slug → true
    const [editing, setEditing] = useState(null);        // slug
    const [draft, setDraft] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [rb, rs] = await Promise.all([
                fetch('/api/catalog/brands', { headers }),
                fetch(`${BRAND_SEO_API}/list`, { headers }),
            ]);
            const brandsData = rb.ok ? await rb.json() : [];
            const seoData = rs.ok ? await rs.json() : [];
            setBrands(brandsData);
            setSeoMap(Object.fromEntries(seoData.map(s => [s.slug, s])));
        } catch (e) {
            toastPush('error', 'Ошибка загрузки: ' + e.message);
        } finally {
            setLoading(false);
        }
    }, [headers, toastPush]);

    useEffect(() => { load(); }, [load]);

    const generate = async (slug) => {
        if (!window.confirm(`Сгенерировать SEO-тексты для бренда через ИИ?\n\nСтоимость: ~0.01 ₽. Время: 3-5 секунд.`)) return;
        setGenerating(g => ({ ...g, [slug]: true }));
        try {
            const r = await fetch(`${BRAND_SEO_API}/generate/${slug}`, {
                method: 'POST',
                headers,
            });
            if (!r.ok) {
                const txt = await r.text();
                throw new Error(txt || `Ошибка ${r.status}`);
            }
            const data = await r.json();
            toastPush('success', `✨ SEO для "${data.brand_label}" сгенерировано (${data.products_count} товаров)`);
            load();
        } catch (e) {
            toastPush('error', 'Ошибка генерации: ' + e.message);
        } finally {
            setGenerating(g => ({ ...g, [slug]: false }));
        }
    };

    const startEdit = (slug) => {
        const seo = seoMap[slug] || {};
        setEditing(slug);
        setDraft({
            seo_title: seo.seo_title || '',
            seo_description: seo.seo_description || '',
            h1: seo.h1 || '',
            intro: seo.intro || '',
        });
    };

    const saveEdit = async () => {
        try {
            const r = await fetch(`${BRAND_SEO_API}/${editing}`, {
                method: 'PATCH',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify(draft),
            });
            if (!r.ok) throw new Error(await r.text());
            toastPush('success', 'SEO сохранено');
            setEditing(null);
            load();
        } catch (e) {
            toastPush('error', 'Ошибка сохранения: ' + e.message);
        }
    };

    const inputCls = 'w-full p-2 border border-[#E8E3DD] rounded-lg text-sm focus:outline-none focus:border-[#6B7A6A]';
    const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

    return (
        <>
            <div className="bg-[#F5F0EB] rounded-xl p-4 mb-4 text-sm text-gray-700">
                <p className="font-semibold mb-1">💡 Как это работает</p>
                <p>ИИ анализирует реальные товары бренда в каталоге и пишет уникальные SEO-тексты. Стоимость: ~0.01 ₽ за бренд. После генерации — <code className="bg-white px-1 rounded">npm run build</code> и деплой.</p>
            </div>

            <div className="flex items-center gap-3 mb-4">
                <button onClick={load} disabled={loading} className="px-3 py-2 rounded-lg border border-[#E8E3DD] text-xs hover:bg-[#F5F0EB] disabled:opacity-50">
                    {loading ? '…' : 'Обновить'}
                </button>
                <span className="text-xs text-gray-500">Всего брендов: {brands.length} · с ИИ-SEO: {Object.keys(seoMap).length}</span>
            </div>

            <div className="space-y-2">
                {brands.map(b => {
                    const seo = seoMap[b.slug];
                    const isGenerating = generating[b.slug];
                    const isEditing = editing === b.slug;

                    return (
                        <div key={b.slug} className="bg-white rounded-xl border border-[#E8E3DD] p-4">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-semibold text-[#1C1917]">{b.label}</span>
                                        <span className="text-xs text-gray-500">/{b.slug}</span>
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#F5F0EB] text-gray-600">{b.count} тов.</span>
                                        {seo?.is_manual && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">ручная правка</span>
                                        )}
                                    </div>
                                    {seo ? (
                                        <p className="text-xs text-gray-600 truncate">
                                            <strong className="text-gray-700">Title:</strong> {seo.seo_title}
                                        </p>
                                    ) : (
                                        <p className="text-xs text-gray-400 italic">SEO не сгенерировано — используется шаблон</p>
                                    )}
                                </div>

                                <div className="flex items-center gap-2 shrink-0">
                                    {seo ? (
                                        <button
                                            onClick={() => isEditing ? saveEdit() : startEdit(b.slug)}
                                            className="px-3 py-1.5 rounded-lg border border-[#E8E3DD] text-xs hover:bg-[#F5F0EB]"
                                        >
                                            {isEditing ? '💾 Сохранить' : '✏️ Править'}
                                        </button>
                                    ) : null}
                                    <button
                                        onClick={() => generate(b.slug)}
                                        disabled={isGenerating}
                                        className="px-3 py-1.5 rounded-lg bg-purple-500 text-white text-xs font-medium hover:bg-purple-600 disabled:opacity-50"
                                    >
                                        {isGenerating ? '⏳ ИИ пишет…' : '✨ Сгенерировать SEO'}
                                    </button>
                                </div>
                            </div>

                            {isEditing && (
                                <div className="mt-3 pt-3 border-t border-[#F0ECE8] space-y-2">
                                    <div>
                                        <label className={labelCls}>Title (до 70 символов)</label>
                                        <input value={draft.seo_title} onChange={e => setDraft(d => ({ ...d, seo_title: e.target.value }))} maxLength={70} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Description (до 160 символов)</label>
                                        <input value={draft.seo_description} onChange={e => setDraft(d => ({ ...d, seo_description: e.target.value }))} maxLength={160} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>H1 (до 60 символов)</label>
                                        <input value={draft.h1} onChange={e => setDraft(d => ({ ...d, h1: e.target.value }))} maxLength={60} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Intro (вводный текст)</label>
                                        <textarea value={draft.intro} onChange={e => setDraft(d => ({ ...d, intro: e.target.value }))} rows={5} className={inputCls} />
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={saveEdit} className="px-3 py-1.5 rounded-lg bg-[#6B7A6A] text-white text-xs">Сохранить</button>
                                        <button onClick={() => setEditing(null)} className="px-3 py-1.5 rounded-lg border border-[#E8E3DD] text-xs">Отмена</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </>
    );
}

/* ==============================
   Основная страница
   ============================== */
export default function AdminPage() {
    useNoindexNofollow();
    const [auth, setAuth] = useState(() => sessionStorage.getItem(AUTH_KEY));
    const { push: toastPush, view: toastView } = useToast();

    const [tab, setTab] = useState('products'); // 'products' | 'orders'

    // --- товары
    const [products, setProducts] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [brand, setBrand] = useState('');
    const [category, setCategory] = useState('');
    const [noKeywords, setNoKeywords] = useState(false);
    const [brokenPhotoIds, setBrokenPhotoIds] = useState(new Set());
    const [onlyBrokenPhotos, setOnlyBrokenPhotos] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkKeywords, setBulkKeywords] = useState('');
    const [bulkAppend, setBulkAppend] = useState(false);
    const [showBulk, setShowBulk] = useState(false);
    const [showNewProduct, setShowNewProduct] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // --- заказы
    const [orders, setOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [ordersStatus, setOrdersStatus] = useState('');
    const [ordersSearch, setOrdersSearch] = useState('');

    const headers = useMemo(() => (auth ? { Authorization: auth } : {}), [auth]);

    const fetchProducts = useCallback(async (forceRefresh = false) => {
        if (!auth) return;
        setLoading(true);
        try {
            if (forceRefresh) {
                await fetch(`${API}/refresh`, { method: 'POST', headers });
            }
            const [rp, rm] = await Promise.all([
                fetch(`${API}/products`, { headers }),
                fetch(`${API}/meta`, { headers }),
            ]);
            if (rp.status === 401 || rm.status === 401) {
                sessionStorage.removeItem(AUTH_KEY);
                setAuth(null);
                return;
            }
            if (!rp.ok) throw new Error(`Сервер вернул ${rp.status} на /products`);
            if (!rm.ok) throw new Error(`Сервер вернул ${rm.status} на /meta`);
            const dp = await rp.json();
            const dm = await rm.json();
            setProducts(dp.items || []);
            setMeta(dm);
        } catch (e) {
            toastPush('error', 'Ошибка загрузки: ' + e.message);
        }
        setLoading(false);
    }, [auth, headers, toastPush]);

    const fetchOrders = useCallback(async () => {
        if (!auth) return;
        setOrdersLoading(true);
        try {
            const qs = new URLSearchParams();
            if (ordersStatus) qs.set('status', ordersStatus);
            const r = await fetch(`${API}/orders?${qs.toString()}`, { headers });
            if (r.status === 401) {
                sessionStorage.removeItem(AUTH_KEY);
                setAuth(null);
                return;
            }
            if (!r.ok) throw new Error(`Сервер вернул ${r.status}`);
            const data = await r.json();
            setOrders(data.items || []);
        } catch (e) {
            toastPush('error', 'Ошибка загрузки заказов: ' + e.message);
        }
        setOrdersLoading(false);
    }, [auth, headers, ordersStatus, toastPush]);

    // НОВОЕ: смена статуса заказа с оптимистичным обновлением UI
    const updateOrderStatus = useCallback(async (orderId, newStatus, extra = {}) => {
        try {
            const r = await fetch(`${API}/orders/${orderId}/status`, {
                method: 'PATCH',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    status: newStatus,
                    tracking_number: extra.tracking_number || undefined,
                    delivery_service: extra.delivery_service || undefined,
                }),
            });
            if (r.status === 401) {
                sessionStorage.removeItem(AUTH_KEY);
                setAuth(null);
                throw new Error('Сессия истекла');
            }
            if (!r.ok) {
                const txt = await r.text().catch(() => '');
                throw new Error(txt || `Сервер вернул ${r.status}`);
            }
            setOrders((prev) =>
                prev.map((o) => o.order_id === orderId ? { ...o, status: newStatus } : o)
            );
            toastPush('success', newStatus === 'SHIPPED'
                ? 'Статус обновлён, письмо с треком отправлено клиенту'
                : 'Статус заказа обновлён');
        } catch (e) {
            toastPush('error', 'Ошибка смены статуса: ' + e.message);
            throw e;
        }
    }, [headers, toastPush]);

    useEffect(() => {
        if (tab === 'products') fetchProducts(false);
    }, [tab, fetchProducts]);

    useEffect(() => {
        if (tab === 'orders') fetchOrders();
    }, [tab, fetchOrders]);

    const filtered = useMemo(() => {
        let list = products;
        if (search) {
            const s = search.toLowerCase();
            list = list.filter(
                (p) =>
                    (p.name || '').toLowerCase().includes(s) ||
                    (p.id || '').includes(s) ||
                    (p.keywords || '').toLowerCase().includes(s)
            );
        }
        if (brand) list = list.filter((p) => p.brand === brand);
        if (category) list = list.filter((p) => p.category === category);
        if (noKeywords) list = list.filter((p) => !(p.keywords || '').trim());
        if (onlyBrokenPhotos) list = list.filter((p) => brokenPhotoIds.has(p.id));
        return list;
    }, [products, search, brand, category, noKeywords, onlyBrokenPhotos, brokenPhotoIds]);

    const filteredOrders = useMemo(() => {
        if (!ordersSearch.trim()) return orders;
        const s = ordersSearch.toLowerCase();
        return orders.filter((o) =>
            (o.order_id || '').toLowerCase().includes(s) ||
            (o.customer_name || '').toLowerCase().includes(s) ||
            (o.phone || '').includes(s) ||
            (o.email || '').toLowerCase().includes(s) ||
            (o.delivery_address || '').toLowerCase().includes(s)
        );
    }, [orders, ordersSearch]);

    const dirtyProducts = useMemo(() => products.filter((p) => p.__dirty), [products]);

    const updateProduct = (updated) => {
        setProducts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    };

    const markBrokenPhoto = useCallback((id) => {
        setBrokenPhotoIds((prev) => {
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
        });
    }, []);

    const saveRow = async (product) => {
        const { __dirty, ...payload } = product;
        const body = {
            name: payload.name,
            price: payload.price,
            category: payload.category,
            brand: payload.brand,
            keywords: payload.keywords,
            description: payload.description,
            photo: payload.photo,
            volume: payload.volume,
            badges: payload.badges,
        };
        try {
            const r = await fetch(`${API}/products/${product.id}`, {
                method: 'PATCH',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!r.ok) throw new Error(await r.text());
            const res = await r.json();
            setProducts((prev) =>
                prev.map((p) =>
                    p.id === product.id ? { ...res.product } : p
                )
            );
            toastPush('success', `Сохранено: ${res.product.name}`);
            fetch(`${API}/meta`, { headers })
                .then((r) => r.json())
                .then(setMeta)
                .catch(() => { });
        } catch (e) {
            toastPush('error', `Ошибка сохранения ${product.id}: ${e.message}`);
        }
    };

    const saveAllDirty = async () => {
        for (const p of dirtyProducts) {
            await saveRow(p);
        }
    };

    const selectAll = (check) => {
        if (check) {
            setSelectedIds(new Set(filtered.map((p) => p.id)));
        } else {
            setSelectedIds(new Set());
        }
    };

    const toggleSelect = (id, check) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (check) next.add(id);
            else next.delete(id);
            return next;
        });
    };

    const applyBulkKeywords = async () => {
        if (!bulkKeywords.trim() || selectedIds.size === 0) return;
        try {
            const r = await fetch(`${API}/products/bulk-keywords`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    product_ids: Array.from(selectedIds),
                    keywords: bulkKeywords.trim(),
                    append: bulkAppend,
                }),
            });
            if (!r.ok) throw new Error(await r.text());
            const res = await r.json();
            toastPush('success', `Обновлено ${res.updated.length} товаров`);
            setSelectedIds(new Set());
            setBulkKeywords('');
            setShowBulk(false);
            fetchProducts();
        } catch (e) {
            toastPush('error', 'Ошибка bulk: ' + e.message);
        }
    };

    const requestDeleteOne = (product) => {
        setDeleteTarget({ ids: [product.id], names: [product.name] });
    };

    const requestDeleteSelected = () => {
        const selected = products.filter((p) => selectedIds.has(p.id));
        setDeleteTarget({
            ids: selected.map((p) => p.id),
            names: selected.map((p) => p.name),
        });
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        const { ids } = deleteTarget;
        let ok = 0;
        let failed = 0;
        for (const id of ids) {
            try {
                const r = await fetch(`${API}/products/${id}`, {
                    method: 'DELETE',
                    headers,
                });
                if (!r.ok) throw new Error(await r.text());
                ok++;
            } catch (e) {
                failed++;
                toastPush('error', `Не удалось удалить ${id}: ${e.message}`);
            }
        }
        if (ok > 0) toastPush('success', `Удалено: ${ok}${failed ? `, ошибок: ${failed}` : ''}`);
        setDeleteTarget(null);
        setSelectedIds(new Set());
        fetchProducts();
    };

    if (!auth) return <LoginGate onAuth={setAuth} />;

    const logout = () => {
        sessionStorage.removeItem(AUTH_KEY);
        setAuth(null);
    };

    const tabBtn = (key, label, badge) => (
        <button
            onClick={() => setTab(key)}
            className={classNames(
                'px-4 py-2 text-sm font-medium rounded-lg transition whitespace-nowrap',
                tab === key
                    ? 'bg-[#6B7A6A] text-white'
                    : 'text-[#1C1917] hover:bg-[#F5F0EB]'
            )}
        >
            {label}
            {badge !== undefined && badge > 0 && (
                <span className={classNames(
                    'ml-2 px-1.5 py-0.5 rounded-full text-[10px]',
                    tab === key ? 'bg-white bg-opacity-25 text-white' : 'bg-[#F5F0EB] text-gray-600'
                )}>
                    {badge}
                </span>
            )}
        </button>
    );

    return (
        <div className="min-h-screen bg-[#FBF9F6]">
            {toastView}

            <NewProductModal
                open={showNewProduct}
                onClose={() => setShowNewProduct(false)}
                onCreate={() => fetchProducts()}
                brands={meta?.brands || []}
                categories={meta?.categories || []}
                headers={headers}
                toastPush={toastPush}
            />

            <ConfirmDialog
                open={!!deleteTarget}
                title={deleteTarget?.ids?.length > 1 ? 'Удалить товары?' : 'Удалить товар?'}
                message={
                    deleteTarget && (
                        <>
                            <p className="mb-2">
                                {deleteTarget.ids.length > 1
                                    ? `Будут удалены ${deleteTarget.ids.length} товара из YDB:`
                                    : 'Товар будет удалён из YDB:'}
                            </p>
                            <ul className="max-h-40 overflow-y-auto text-xs bg-[#F5F0EB] rounded p-2 space-y-1">
                                {deleteTarget.names.slice(0, 20).map((n, i) => (
                                    <li key={i} className="truncate">• {n}</li>
                                ))}
                                {deleteTarget.names.length > 20 && (
                                    <li className="text-gray-500">…и ещё {deleteTarget.names.length - 20}</li>
                                )}
                            </ul>
                            <p className="mt-3 text-xs text-red-600 font-medium">
                                Действие нельзя отменить.
                            </p>
                        </>
                    )
                }
                confirmLabel="Удалить"
                danger
                onConfirm={confirmDelete}
                onCancel={() => setDeleteTarget(null)}
            />

            {/* Шапка */}
            <header className="bg-white border-b border-[#E8E3DD] sticky top-0 z-50">
                <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center gap-4 flex-wrap">
                    <div className="flex items-baseline gap-2">
                        <span className="serif text-xl">Pure Synergy</span>
                        <span className="text-xs text-gray-400 uppercase tracking-widest">admin</span>
                    </div>

                    <nav className="flex items-center gap-1 sm:ml-6">
                        {tabBtn('products', 'Товары', meta?.total)}
                        {tabBtn('orders', 'Заказы', orders.length || undefined)}
                        {tabBtn('promotions', 'Акции')}
                        {tabBtn('clients', 'Клиенты')}
                        {tabBtn('bonus', 'Бонусы')}
                        {tabBtn('brands', 'Бренды')}
                    </nav>

                    <div className="ml-auto flex items-center gap-3 text-sm">
                        {tab === 'products' && meta && (
                            <span className="text-gray-500 hidden sm:inline">
                                Всего: <strong className="text-[#1C1917]">{meta.total}</strong>
                                {meta.no_keywords > 0 && (
                                    <>
                                        {' · '}
                                        <span className="text-red-600 font-medium">
                                            без keywords: {meta.no_keywords}
                                        </span>
                                    </>
                                )}
                            </span>
                        )}
                        {tab === 'products' && dirtyProducts.length > 0 && (
                            <button
                                onClick={saveAllDirty}
                                className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-medium hover:bg-amber-600"
                            >
                                Сохранить всё ({dirtyProducts.length})
                            </button>
                        )}
                        {tab === 'products' && (
                            <button
                                onClick={() => setShowNewProduct(true)}
                                className="px-3 py-1.5 rounded-lg bg-[#6B7A6A] text-white text-xs font-medium hover:bg-[#5a6a59]"
                            >
                                + Добавить товар
                            </button>
                        )}
                        <button
                            onClick={() => tab === 'products' ? fetchProducts(true) : fetchOrders()}
                            disabled={loading || ordersLoading}
                            className="px-3 py-1.5 rounded-lg border border-[#E8E3DD] text-xs hover:bg-[#F5F0EB] disabled:opacity-50"
                        >
                            {loading || ordersLoading ? '…' : 'Обновить'}
                        </button>
                        <button
                            onClick={logout}
                            className="px-3 py-1.5 rounded-lg border border-[#E8E3DD] text-xs hover:bg-[#F5F0EB]"
                        >
                            Выйти
                        </button>
                    </div>
                </div>

                {/* Фильтры для товаров */}
                {tab === 'products' && (
                    <>
                        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center gap-3 flex-wrap border-t border-[#F0ECE8]">
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Поиск по названию, ID или keywords…"
                                className="flex-1 min-w-[220px] p-2 border border-[#E8E3DD] rounded-lg text-sm focus:outline-none focus:border-[#6B7A6A]"
                            />
                            <select
                                value={brand}
                                onChange={(e) => setBrand(e.target.value)}
                                className="p-2 border border-[#E8E3DD] rounded-lg text-sm bg-white"
                            >
                                <option value="">Все бренды</option>
                                {meta?.brands?.map((b) => (
                                    <option key={b} value={b}>{b}</option>
                                ))}
                            </select>
                            <select
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                className="p-2 border border-[#E8E3DD] rounded-lg text-sm bg-white"
                            >
                                <option value="">Все категории</option>
                                {meta?.categories?.map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={noKeywords}
                                    onChange={(e) => setNoKeywords(e.target.checked)}
                                />
                                Только без keywords
                            </label>
                            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={onlyBrokenPhotos}
                                    onChange={(e) => setOnlyBrokenPhotos(e.target.checked)}
                                />
                                Только мёртвые фото {brokenPhotoIds.size > 0 && `(${brokenPhotoIds.size})`}
                            </label>
                            <span className="text-xs text-gray-500 ml-auto">
                                Показано: <strong>{filtered.length}</strong>
                            </span>
                        </div>

                        {selectedIds.size > 0 && (
                            <div className="max-w-[1600px] mx-auto px-4 py-2 bg-[#F5F0EB] border-t border-[#E8E3DD] flex items-center gap-3 flex-wrap text-sm">
                                <span className="font-medium">Выбрано: {selectedIds.size}</span>
                                {!showBulk ? (
                                    <button
                                        onClick={() => setShowBulk(true)}
                                        className="px-3 py-1.5 rounded-lg bg-[#6B7A6A] text-white text-xs hover:bg-[#5a6a59]"
                                    >
                                        Проставить keywords
                                    </button>
                                ) : (
                                    <>
                                        <input
                                            value={bulkKeywords}
                                            onChange={(e) => setBulkKeywords(e.target.value)}
                                            placeholder="увлажнение, барьер, сухая кожа"
                                            className="flex-1 min-w-[260px] p-2 border border-[#E8E3DD] rounded-lg text-sm"
                                        />
                                        <label className="flex items-center gap-1 text-xs">
                                            <input
                                                type="checkbox"
                                                checked={bulkAppend}
                                                onChange={(e) => setBulkAppend(e.target.checked)}
                                            />
                                            Добавить к существующим
                                        </label>
                                        <button
                                            onClick={applyBulkKeywords}
                                            disabled={!bulkKeywords.trim()}
                                            className="px-3 py-1.5 rounded-lg bg-[#6B7A6A] text-white text-xs disabled:opacity-50"
                                        >
                                            Применить
                                        </button>
                                        <button
                                            onClick={() => { setShowBulk(false); setBulkKeywords(''); }}
                                            className="px-3 py-1.5 rounded-lg border border-[#E8E3DD] text-xs"
                                        >
                                            Отмена
                                        </button>
                                    </>
                                )}
                                <button
                                    onClick={requestDeleteSelected}
                                    className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs hover:bg-red-600"
                                >
                                    🗑 Удалить выбранные
                                </button>
                                <button
                                    onClick={() => setSelectedIds(new Set())}
                                    className="ml-auto text-xs text-gray-500 hover:text-[#1C1917] underline"
                                >
                                    Снять выделение
                                </button>
                            </div>
                        )}
                    </>
                )}

                {/* Фильтры для заказов */}
                {tab === 'orders' && (
                    <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center gap-3 flex-wrap border-t border-[#F0ECE8]">
                        <input
                            value={ordersSearch}
                            onChange={(e) => setOrdersSearch(e.target.value)}
                            placeholder="Поиск: номер, клиент, телефон, email, адрес…"
                            className="flex-1 min-w-[260px] p-2 border border-[#E8E3DD] rounded-lg text-sm focus:outline-none focus:border-[#6B7A6A]"
                        />
                        {/* НОВОЕ: расширенный список статусов для фильтра */}
                        <select
                            value={ordersStatus}
                            onChange={(e) => setOrdersStatus(e.target.value)}
                            className="p-2 border border-[#E8E3DD] rounded-lg text-sm bg-white"
                        >
                            <option value="">Все статусы</option>
                            <option value="CREATED">Создан</option>
                            <option value="PENDING_PAYMENT">Ожидает оплаты</option>
                            <option value="PAID">Оплачен</option>
                            <option value="SHIPPED">Отправлен</option>
                            <option value="DELIVERED">Доставлен</option>
                            <option value="RETURN_REQUESTED">Возврат запрошен</option>
                            <option value="RETURNED">Возврат</option>
                            <option value="REFUNDED">Деньги возвращены</option>
                            <option value="CANCELLED">Отменён</option>
                        </select>
                        <span className="text-xs text-gray-500 ml-auto">
                            Показано: <strong>{filteredOrders.length}</strong>
                        </span>
                    </div>
                )}
            </header>

            {/* Контент */}
            <main className="max-w-[1600px] mx-auto px-4 py-6">
                {tab === 'products' && (
                    <>
                        <div className="bg-white rounded-2xl border border-[#E8E3DD] overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-[#FBF9F6] text-xs text-gray-500 uppercase tracking-wider">
                                        <tr>
                                            <th className="p-2 w-8">
                                                <input
                                                    type="checkbox"
                                                    checked={
                                                        filtered.length > 0 &&
                                                        filtered.every((p) => selectedIds.has(p.id))
                                                    }
                                                    onChange={(e) => selectAll(e.target.checked)}
                                                />
                                            </th>
                                            <th className="text-left p-2 font-medium">ID</th>
                                            <th className="text-left p-2 font-medium">Фото</th>
                                            <th className="text-left p-2 font-medium">Название</th>
                                            <th className="text-right p-2 font-medium w-24">Цена</th>
                                            <th className="text-left p-2 font-medium">Категория</th>
                                            <th className="text-left p-2 font-medium">Бренд</th>
                                            <th className="text-left p-2 font-medium">Ключевые слова</th>
                                            <th className="text-left p-2 font-medium">Описание</th>
                                            <th className="p-2 w-12"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.length === 0 && (
                                            <tr>
                                                <td colSpan={10} className="text-center py-12 text-gray-400">
                                                    Ничего не найдено
                                                </td>
                                            </tr>
                                        )}
                                        {filtered.map((p) => (
                                            <ProductRow
                                                key={p.id}
                                                product={p}
                                                selected={selectedIds.has(p.id)}
                                                onSelect={(c) => toggleSelect(p.id, c)}
                                                onChange={updateProduct}
                                                onDelete={() => requestDeleteOne(p)}
                                                onBrokenPhoto={markBrokenPhoto}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <p className="text-xs text-gray-400 mt-3">
                            Клик по ячейке → редактирование.{' '}
                            <kbd className="px-1 border rounded">Enter</kbd> — сохранить строку,{' '}
                            <kbd className="px-1 border rounded">Esc</kbd> — отменить.
                            Строки с изменениями подсвечиваются жёлтым.
                            Для длинных описаний — кнопки «Показать полностью» и «Редактировать».
                            Удаление — через иконку 🗑 справа или массово через выделение.
                        </p>
                    </>
                )}

                {tab === 'orders' && (
                    <div className="bg-white rounded-2xl border border-[#E8E3DD] overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-[#FBF9F6] text-xs text-gray-500 uppercase tracking-wider">
                                    <tr>
                                        <th className="text-left p-2 font-medium whitespace-nowrap">Дата</th>
                                        <th className="text-left p-2 font-medium">Заказ</th>
                                        <th className="text-left p-2 font-medium">Клиент</th>
                                        <th className="text-left p-2 font-medium">Состав</th>
                                        <th className="text-right p-2 font-medium whitespace-nowrap">Сумма</th>
                                        <th className="text-left p-2 font-medium">Доставка</th>
                                        <th className="text-left p-2 font-medium">Статус</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredOrders.length === 0 && (
                                        <tr>
                                            <td colSpan={7} className="text-center py-12 text-gray-400">
                                                {ordersLoading ? 'Загрузка…' : 'Заказов нет'}
                                            </td>
                                        </tr>
                                    )}
                                    {filteredOrders.map((o) => (
                                        <OrderRow
                                            key={o.order_id}
                                            order={o}
                                            onStatusChange={updateOrderStatus}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                {tab === 'promotions' && (
                    <PromotionsTab headers={headers} meta={meta} toastPush={toastPush} />
                )}
                {tab === 'clients' && (
                    <ClientsTab headers={headers} toastPush={toastPush} />
                )}
                {tab === 'bonus' && (
                    <BonusTab headers={headers} toastPush={toastPush} />
                )}
                {tab === 'brands' && (
                    <BrandsTab headers={headers} toastPush={toastPush} />
                )}
            </main>
        </div>
    );
}